/**
 * Upgrades the legacy theme's FAQ accordion, as it survives inside article
 * rich-text, into the theme's own disclosure row.
 *
 * The markup Shopify returns for `article.content` on "Our sustainable journey"
 * is the previous theme's accordion, authored once and stored forever:
 *
 *   <dl class="faqAccordion">
 *     <dt><button type="button" aria-controls="panel-01" aria-expanded="true">1. …</button></dt>
 *     <dd id="panel-01" aria-hidden="false">…</dd>
 *     …nine more, every one of them id="panel-01"…
 *   </dl>
 *
 * Horizon has no handler for it, so each question renders as a bare UA button
 * that does nothing while claiming `aria-expanded` — a control that lies to
 * assistive technology — and the duplicated ids make every `aria-controls`
 * point at the same element. Neither is fixable from CSS, and the content
 * cannot be rewritten from the theme.
 *
 * So each dt/dd pair is rebuilt as the `<details class="r-disclosure">` row
 * `sections/r-faq.liquid` uses: the DISCLOSURE band in r-base.css owns the
 * open/close tempo, r-disclosure.js carries the engines without
 * interpolate-size, and `<summary>` gives the row real keyboard semantics.
 * Rows are built OPEN, matching the authored `aria-expanded="true"` — nothing
 * that is readable today gets hidden by this running.
 *
 * Progressive enhancement: if the module never loads, the raw <dl> is styled to
 * read as a plain question/answer list by the LEGACY FAQ ACCORDION block in
 * assets/r-article.css.
 */

/** @param {HTMLDListElement} dl */
function upgrade(dl) {
  const icon = document.getElementById('r-rte-faq-icon');
  /** @type {{ dt: Element, dds: Element[] }[]} */
  const rows = [];
  for (const child of Array.from(dl.children)) {
    if (child.tagName === 'DT') rows.push({ dt: child, dds: [] });
    else if (child.tagName === 'DD' && rows.length) rows[rows.length - 1].dds.push(child);
  }
  // A <dl> with no answers is a definition list, not an accordion; leave it be.
  if (!rows.length || !rows.some((r) => r.dds.length)) return;

  const wrap = document.createElement('div');
  wrap.className = 'r-rte-faq';

  for (const row of rows) {
    const details = document.createElement('details');
    details.className = 'r-disclosure r-rte-faq__row';
    details.open = true;

    const summary = document.createElement('summary');
    summary.className = 'r-rte-faq__question';

    // A real heading per question: the outline a text extractor builds from the
    // article then lists all ten, which is the same reason r-faq.liquid puts an
    // h3 inside its summary.
    const heading = document.createElement('h3');
    heading.className = 'r-rte-faq__question-text';
    heading.textContent = (row.dt.querySelector('button') ?? row.dt).textContent?.trim() ?? '';
    summary.append(heading);

    if (icon instanceof HTMLTemplateElement) {
      const mark = document.createElement('span');
      mark.className = 'svg-wrapper icon-plus r-rte-faq__icon';
      mark.append(icon.content.cloneNode(true));
      summary.append(mark);
    }

    const panel = document.createElement('div');
    panel.className = 'r-disclosure__panel r-rte-faq__answer';
    const copy = document.createElement('div');
    copy.className = 'r-rte-faq__answer-copy';
    // Move the nodes rather than copying innerHTML: the answers carry links and
    // <em>, and moving keeps them the same nodes the page already parsed.
    for (const dd of row.dds) while (dd.firstChild) copy.append(dd.firstChild);
    panel.append(copy);

    details.append(summary, panel);
    wrap.append(details);
  }

  dl.replaceWith(wrap);
}

/**
 * Exported as well as self-invoked: the module only fires once, on load, so
 * anything that injects article markup afterwards (a section re-render, a
 * console-driven check against a real article body) needs a way to re-run it.
 */
export function upgradeAll(root = document) {
  for (const dl of root.querySelectorAll('.r-article-body dl')) {
    // `faqAccordion` is the legacy class; the `dt > button` test catches the
    // same structure authored without it.
    if (dl.classList.contains('faqAccordion') || dl.querySelector(':scope > dt > button')) {
      upgrade(/** @type {HTMLDListElement} */ (dl));
    }
  }
}

upgradeAll();
